import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BlogBackToTopButton from '../components/BlogBackToTopButton';
import { API_BASE_URL, apiUrl } from '../config';

function formatDate(value) {
  if (!value) return '';
  try {
    return new Intl.DateTimeFormat('tr-TR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    }).format(new Date(value));
  } catch (_) {
    return '';
  }
}

function formatMetaLine(post) {
  const date = formatDate(post?.publishedAt);
  if (!date) return `${post?.readingTimeMinutes || 0} dk okuma`;
  return `${date.toLocaleUpperCase('tr-TR')} • ${post?.readingTimeMinutes || 0} dk okuma`;
}

function stripHtmlText(html) {
  return String(html || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function decodeEntities(value) {
  const parser = document.createElement('textarea');
  parser.innerHTML = value;
  return parser.value;
}

function resolveBlogImageUrl(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (raw.startsWith('/uploads/') || raw.startsWith('/api/uploads/')) return `${API_BASE_URL}${raw}`;
  if (raw.startsWith('uploads/')) return `${API_BASE_URL}/${raw}`;
  return raw;
}

function resolveAbsoluteContentUrl(value, fallback = '') {
  const raw = String(value || fallback || '').trim();
  if (!raw) return '';
  if (/^https?:\/\//i.test(raw)) return raw;
  if (raw.startsWith('/')) return `${window.location.origin}${raw}`;
  return `${window.location.origin}/${raw.replace(/^\/+/, '')}`;
}

function slugifyHeadingId(value) {
  return String(value || '')
    .toLocaleLowerCase('tr-TR')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s-]/g, ' ')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

function enhanceBlogHtml(rawHtml) {
  const html = String(rawHtml || '').trim();
  if (!html || typeof document === 'undefined') {
    return { html, headings: [] };
  }

  const wrapper = document.createElement('div');
  wrapper.innerHTML = html;

  const usedIds = new Map();
  const headings = [];

  wrapper.querySelectorAll('h2, h3').forEach((node) => {
    const text = stripHtmlText(node.textContent || '');
    if (!text) return;
    const baseId = slugifyHeadingId(text) || 'bolum';
    const count = usedIds.get(baseId) || 0;
    usedIds.set(baseId, count + 1);
    const id = count === 0 ? baseId : `${baseId}-${count + 1}`;
    node.setAttribute('id', id);
    if (node.tagName.toLowerCase() === 'h2') {
      headings.push({ id, title: text });
    }
  });

  const textNodes = [];
  const walker = document.createTreeWalker(wrapper, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const parentTag = node.parentElement?.tagName?.toLowerCase();
      if (!node.nodeValue?.trim()) return NodeFilter.FILTER_REJECT;
      if (['a', 'script', 'style', 'pre', 'code'].includes(parentTag)) {
        return NodeFilter.FILTER_REJECT;
      }
      return NodeFilter.FILTER_ACCEPT;
    }
  });

  while (walker.nextNode()) {
    textNodes.push(walker.currentNode);
  }

  const linkPattern = /(https?:\/\/[^\s<]+|\/blog\/[^\s<]+)/gi;

  textNodes.forEach((node) => {
    const text = node.nodeValue || '';
    linkPattern.lastIndex = 0;
    if (!linkPattern.test(text)) return;

    const fragment = document.createDocumentFragment();
    let lastIndex = 0;

    text.replace(linkPattern, (match, _group, offset) => {
      if (offset > lastIndex) {
        fragment.appendChild(document.createTextNode(text.slice(lastIndex, offset)));
      }

      const anchor = document.createElement('a');
      const href = match.startsWith('http') ? match : resolveAbsoluteContentUrl(match);
      anchor.setAttribute('href', href);
      if (match.startsWith('http')) {
        anchor.setAttribute('target', '_blank');
        anchor.setAttribute('rel', 'noreferrer');
      }
      anchor.textContent = match;
      fragment.appendChild(anchor);
      lastIndex = offset + match.length;
      return match;
    });

    if (lastIndex < text.length) {
      fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
    }

    node.parentNode?.replaceChild(fragment, node);
  });

  return {
    html: wrapper.innerHTML,
    headings
  };
}

function buildHowToJsonLd(post) {
  const headings = Array.from(String(post?.htmlContent || '').matchAll(/<h[2-3][^>]*>(.*?)<\/h[2-3]>/gi))
    .map((match) => decodeEntities(stripHtmlText(match[1])))
    .filter(Boolean);

  if (!headings.length) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: post.title,
    description: post.seoMetaDescription,
    step: headings.map((heading, index) => ({
      '@type': 'HowToStep',
      position: index + 1,
      name: heading,
      text: heading
    }))
  };
}

function buildFaqJsonLd(post) {
  const headings = Array.from(String(post?.htmlContent || '').matchAll(/<h[2-3][^>]*>(.*?)<\/h[2-3]>/gi))
    .map((match) => decodeEntities(stripHtmlText(match[1])))
    .filter(Boolean);

  if (!headings.length) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: headings.map((heading) => ({
      '@type': 'Question',
      name: heading,
      acceptedAnswer: {
        '@type': 'Answer',
        text: post.seoMetaDescription || heading
      }
    }))
  };
}

export default function BlogDetailPage({ isDarkMode, toggleDarkMode }) {
  const { categorySlug, postSlug } = useParams();
  const [payload, setPayload] = useState({ post: null, relatedPosts: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [shareMessage, setShareMessage] = useState('');
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const res = await fetch(apiUrl(`/api/blog/categories/${categorySlug}/posts/${postSlug}`));
        if (!res.ok) throw new Error('blog_detail_failed');
        const data = await res.json();
        if (!mounted) return;
        setPayload({
          post: data?.post || null,
          relatedPosts: Array.isArray(data?.relatedPosts) ? data.relatedPosts : []
        });
      } catch (_error) {
        if (!mounted) return;
        setError('Yazı şu anda yüklenemedi.');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [categorySlug, postSlug]);

  useEffect(() => {
    if (!isLightboxOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsLightboxOpen(false);
      }
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isLightboxOpen]);

  const jsonLdPayloads = useMemo(() => {
    const post = payload.post;
    if (!post) return [];

    const canonicalUrl = resolveAbsoluteContentUrl(
      post.canonicalUrl,
      `/blog/${post.categorySlug}/${post.slug}`
    );
    const categoryUrl = post.categoryCanonicalUrl
      ? `${window.location.origin}${post.categoryCanonicalUrl}`
      : `${window.location.origin}/blog/${post.categorySlug}`;

    const article = {
      '@context': 'https://schema.org',
      '@type': post.jsonLdType === 'HowTo' || post.jsonLdType === 'FAQPage' ? 'Article' : (post.jsonLdType || 'Article'),
      headline: post.seoMetaTitle || post.title,
      description: post.seoMetaDescription,
      image: resolveBlogImageUrl(post.ogImageUrl || post.coverImageUrl) || undefined,
      datePublished: post.publishedAt || undefined,
      author: post.authorName ? { '@type': 'Person', name: post.authorName } : undefined,
      mainEntityOfPage: canonicalUrl
    };

    const breadcrumb = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Anasayfa',
          item: window.location.origin
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Blog',
          item: `${window.location.origin}/blog`
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: post.categoryName || 'Kategori',
          item: categoryUrl
        },
        {
          '@type': 'ListItem',
          position: 4,
          name: post.title,
          item: canonicalUrl
        }
      ]
    };

    const extra =
      post.jsonLdType === 'HowTo'
        ? buildHowToJsonLd(post)
        : post.jsonLdType === 'FAQPage'
          ? buildFaqJsonLd(post)
          : null;

    return [article, breadcrumb, extra].filter(Boolean);
  }, [payload.post]);

  const enhancedContent = useMemo(
    () => enhanceBlogHtml(payload.post?.htmlContent || ''),
    [payload.post]
  );

  useEffect(() => {
    const post = payload.post;
    if (!post) return undefined;

    const canonicalUrl = resolveAbsoluteContentUrl(
      post.canonicalUrl,
      `/blog/${post.categorySlug}/${post.slug}`
    );
    const absoluteOgImageUrl = resolveBlogImageUrl(post.ogImageUrl || post.coverImageUrl || '');
    document.title = `${post.seoMetaTitle || post.title} | BBTV Blog`;

    const ensureMeta = (selector, attrs) => {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        Object.entries(attrs.match || {}).forEach(([key, value]) => el.setAttribute(key, value));
        document.head.appendChild(el);
      }
      Object.entries(attrs.set || {}).forEach(([key, value]) => el.setAttribute(key, value));
      return el;
    };

    const ensureLink = (selector, rel, href) => {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        document.head.appendChild(el);
      }
      el.setAttribute('href', href);
      return el;
    };

    ensureMeta('meta[name="description"]', {
      match: { name: 'description' },
      set: { content: post.seoMetaDescription || '' }
    });
    ensureMeta('meta[property="og:title"]', {
      match: { property: 'og:title' },
      set: { content: post.seoMetaTitle || post.title }
    });
    ensureMeta('meta[property="og:description"]', {
      match: { property: 'og:description' },
      set: { content: post.seoMetaDescription || '' }
    });
    ensureMeta('meta[property="og:type"]', {
      match: { property: 'og:type' },
      set: { content: 'article' }
    });
    ensureMeta('meta[property="og:url"]', {
      match: { property: 'og:url' },
      set: { content: canonicalUrl }
    });
    ensureMeta('meta[property="og:image"]', {
      match: { property: 'og:image' },
      set: { content: absoluteOgImageUrl }
    });
    ensureMeta('meta[name="twitter:card"]', {
      match: { name: 'twitter:card' },
      set: { content: absoluteOgImageUrl ? 'summary_large_image' : 'summary' }
    });
    ensureMeta('meta[name="twitter:title"]', {
      match: { name: 'twitter:title' },
      set: { content: post.seoMetaTitle || post.title }
    });
    ensureMeta('meta[name="twitter:description"]', {
      match: { name: 'twitter:description' },
      set: { content: post.seoMetaDescription || '' }
    });
    ensureMeta('meta[name="twitter:image"]', {
      match: { name: 'twitter:image' },
      set: { content: absoluteOgImageUrl }
    });
    ensureMeta('meta[name="robots"]', {
      match: { name: 'robots' },
      set: { content: post.isIndexed === false ? 'noindex, nofollow' : 'index, follow' }
    });
    ensureLink('link[rel="canonical"]', 'canonical', canonicalUrl);

    const existingScripts = Array.from(document.querySelectorAll('[data-blog-jsonld="true"]'));
    existingScripts.forEach((script) => script.parentNode?.removeChild(script));

    jsonLdPayloads.forEach((jsonLd, index) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.dataset.blogJsonld = 'true';
      script.dataset.blogJsonldIndex = String(index);
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    });

    return () => {
      const latestScripts = Array.from(document.querySelectorAll('[data-blog-jsonld="true"]'));
      latestScripts.forEach((script) => script.parentNode?.removeChild(script));
    };
  }, [jsonLdPayloads, payload.post]);

  const handleShare = async () => {
    const post = payload.post;
    if (!post) return;

    const shareUrl = `${window.location.origin}${post.canonicalUrl || `/blog/${post.categorySlug}/${post.slug}`}`;
    setShareMessage('');

    try {
      if (navigator.share) {
        await navigator.share({
          title: post.title,
          text: post.seoMetaDescription || post.excerpt,
          url: shareUrl
        });
        return;
      }

      await navigator.clipboard.writeText(shareUrl);
      setShareMessage('Bağlantı kopyalandı.');
    } catch (_error) {
      setShareMessage('Bağlantı paylaşılamadı.');
    }
  };

  const post = payload.post;

  return (
    <div className="min-h-screen bg-white text-slate-900 dark:bg-[#1a1a1a] dark:text-white">
      <Header isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />

      <main className="mx-auto mt-8 w-full max-w-[860px] px-4 pb-20 pt-24 md:px-6 md:pt-28">
        {loading ? (
          <section className="rounded-xl border border-slate-200 bg-slate-50 p-8 text-sm dark:border-slate-800 dark:bg-slate-900/60">
            Yazı yükleniyor...
          </section>
        ) : error || !post ? (
          <section className="rounded-xl border border-red-200 bg-red-50 p-8 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-950/20 dark:text-red-300">
            {error || 'Yazı bulunamadı.'}
          </section>
        ) : (
          <>
            <nav className="mb-4 flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
              <Link to="/" className="hover:text-primary">
                Anasayfa
              </Link>
              <span className="material-symbols-outlined text-sm">chevron_right</span>
              <Link to="/blog" className="hover:text-primary">
                Blog
              </Link>
              <span className="material-symbols-outlined text-sm">chevron_right</span>
              <Link to={post.categoryCanonicalUrl || `/blog/${post.categorySlug}`} className="hover:text-primary">
                {post.categoryName || 'İçerik'}
              </Link>
              <span className="material-symbols-outlined text-sm">chevron_right</span>
              <span>{post.title}</span>
            </nav>

            <header className="mb-8">
              <h1 className="mb-4 font-serif text-3xl font-black leading-tight tracking-tight text-slate-950 dark:text-white md:text-5xl">
                {post.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 border-y border-slate-200 py-4 dark:border-slate-800">
                <div className="h-12 w-12 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                  {post.authorPhotoUrl ? (
                    <img
                      src={post.authorPhotoUrl}
                      alt={post.authorName || 'Yazar'}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-sm font-black text-primary">
                      BB
                    </div>
                  )}
                </div>

                <div className="flex flex-col">
                  <span className="font-bold text-slate-950 dark:text-white">
                    {post.authorName || 'BBTV Editörü'}
                  </span>
                  <span className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                    {formatMetaLine(post)}
                  </span>
                </div>

                <div className="ml-auto flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleShare}
                    aria-label="Paylaş"
                    className="rounded border border-slate-300 p-2 transition-colors hover:bg-slate-100 dark:border-slate-700 dark:hover:bg-slate-800"
                  >
                    <span className="material-symbols-outlined text-base text-slate-600 dark:text-slate-300">share</span>
                  </button>
                </div>
              </div>
              {shareMessage ? (
                <div className="mt-3 text-sm text-primary">{shareMessage}</div>
              ) : null}
            </header>

            <div className="mb-20 overflow-hidden rounded-xl border border-slate-200 bg-slate-100 shadow-sm dark:border-slate-800 dark:bg-slate-900">
              {post.coverImageUrl ? (
                <button
                  type="button"
                  onClick={() => setIsLightboxOpen(true)}
                  className="group block w-full text-left"
                  aria-label="Kapak görselini büyüt"
                >
                  <img
                    src={resolveBlogImageUrl(post.coverImageUrl)}
                    alt={post.coverImageAlt || post.title}
                    className="h-auto w-full object-cover transition duration-300 group-hover:scale-[1.01]"
                  />
                </button>
              ) : (
                <div className="flex aspect-[16/9] items-center justify-center text-sm text-slate-400">
                  Kapak görseli
                </div>
              )}
            </div>

            {enhancedContent.headings.length > 0 ? (
              <section className="mx-auto mb-12 max-w-[760px] rounded-[1.75rem] border border-slate-200 bg-[linear-gradient(180deg,_rgba(248,250,252,0.96),_rgba(255,255,255,0.98))] p-4 md:p-6 dark:border-slate-800 dark:bg-[linear-gradient(180deg,_rgba(15,23,42,0.82),_rgba(2,6,23,0.9))]">
                <div className="mb-4 text-[11px] font-black uppercase tracking-[0.28em] text-primary">
                  Bu Yazıda
                </div>
                <div className="space-y-2">
                  {enhancedContent.headings.slice(0, 6).map((heading, index) => (
                    <a
                      key={heading.id}
                      href={`#${heading.id}`}
                      className="group flex items-start gap-3 md:gap-4 rounded-2xl border border-slate-200 bg-white/90 px-3 py-3 md:px-4 text-slate-700 transition hover:border-primary/40 hover:bg-white dark:border-slate-800 dark:bg-slate-950/80 dark:text-slate-200 dark:hover:bg-slate-950"
                    >
                      <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[11px] font-black text-primary">
                        {index + 1}
                      </span>
                      <span className="text-sm md:text-[15px] font-semibold leading-6 transition group-hover:text-primary">
                        {heading.title}
                      </span>
                    </a>
                  ))}
                </div>
              </section>
            ) : null}

            <article
              className="blog-article-content mx-auto max-w-[760px]"
              dangerouslySetInnerHTML={{ __html: enhancedContent.html || post.htmlContent || '' }}
            />

            <section className="mt-20 rounded-xl border border-slate-200 p-8 text-center dark:border-slate-800">
              <h3 className="mb-2 font-serif text-3xl font-bold tracking-tight text-slate-950 dark:text-white">
                BBTV'de Bu Akışı Dene
              </h3>
              <p className="mb-4 text-slate-600 dark:text-slate-300">
                Tarif aramaktan öte, mutfağını stok, bütçe ve günlük rutin üzerinden yönetmek için BBTV deneyimini aç.
              </p>
              <div className="flex justify-center">
                <Link
                  to="/kayit-ol"
                  className="rounded-lg bg-primary px-6 py-3 text-sm font-bold text-white transition-transform hover:scale-[0.98]"
                >
                  Kayıt Ol
                </Link>
              </div>
            </section>

            {(payload.relatedPosts || []).length > 0 && (
              <section className="mt-20 border-t border-slate-200 pt-8 dark:border-slate-800">
                <h2 className="mb-5 font-serif text-3xl font-bold tracking-tight text-slate-950 dark:text-white">
                  İlgili içerikler
                </h2>
                <div className="grid gap-5 md:grid-cols-2">
                  {payload.relatedPosts.map((relatedPost) => (
                    <Link
                      key={relatedPost.id}
                      to={relatedPost.canonicalUrl || `/blog/${relatedPost.categorySlug}/${relatedPost.slug}`}
                      className="rounded-xl border border-slate-200 bg-white p-5 transition hover:-translate-y-1 dark:border-slate-800 dark:bg-slate-900"
                    >
                      <div className="mb-2 text-[11px] font-black uppercase tracking-[0.18em] text-primary">
                        {relatedPost.categoryName || 'İçerik'}
                      </div>
                      <h3 className="font-serif text-2xl font-bold tracking-tight text-slate-950 dark:text-white">
                        {relatedPost.title}
                      </h3>
                      <p className="mt-3 text-sm leading-7 text-slate-600 dark:text-slate-300">
                        {relatedPost.excerpt}
                      </p>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </main>

      {isLightboxOpen && post?.coverImageUrl ? (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center bg-black/85 p-4 backdrop-blur-sm"
          onClick={() => setIsLightboxOpen(false)}
        >
          <button
            type="button"
            onClick={() => setIsLightboxOpen(false)}
            className="absolute right-4 top-4 rounded-full border border-white/20 bg-white/10 px-3 py-2 text-sm font-bold text-white transition hover:bg-white/20"
            aria-label="Görseli kapat"
          >
            Kapat
          </button>
          <img
            src={resolveBlogImageUrl(post.coverImageUrl)}
            alt={post.coverImageAlt || post.title}
            className="max-h-[88vh] max-w-[min(96vw,1400px)] rounded-2xl object-contain shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          />
        </div>
      ) : null}

      <BlogBackToTopButton />
      <Footer />
    </div>
  );
}
